import React, { useEffect, useMemo, useState } from "react";
import { errorLogger } from "@/Common/helpers/logger";
import { updateSystemSettings } from "@/Settings/api/SystemSettings/mutations";
import { Grid } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import MultipleSelectGroup from "@/Layouts/components/MultipleSelectGroup/MultipleSelectGroup";
import isEqual from "lodash/isEqual";

type TTechnicalContactsProps = {
	disabled: boolean;
	contacts?: string[];
	locationId: string;
};

const compareAlphabetically = (a: string, b: string) => {
	return a.localeCompare(b);
};

const TechnicalContacts = React.memo((props: TTechnicalContactsProps) => {
	const { locationId, disabled, contacts = [] } = props;
	const [internalContacts, setInternalContacts] = useState<string[]>([]);

	const queryClient = useQueryClient();

	const { getUsersSystemSettings: users = [] } = queryClient.getQueryData(["systemSettingsAdditionalInfo", locationId]) as {
		getUsersSystemSettings: { _id: string; fullName: string; username: string }[];
	};

	const possibleContacts = useMemo(
		() =>
			users?.map((item) => {
				return {
					_id: item._id,
					label: item.fullName,
				};
			}),
		[users]
	).filter((item) => !internalContacts.includes(item._id));

	const { mutate, isPending } = useMutation({
		mutationFn: (input: string[]) => updateSystemSettings(locationId, { technicalContacts: input }, "Technical Contacts"),
		onError: (error) => errorLogger(error),
	});

	const handleUpdate = (value: { _id: string; label: string }, position: number) => {
		const newContacts = [...internalContacts];
		newContacts[position] = value._id;
		setInternalContacts(newContacts);
		if (newContacts.every(Boolean) && !isEqual(newContacts.sort(compareAlphabetically), contacts.sort(compareAlphabetically))) {
			mutate(newContacts);
		}
	};

	const handleAdd = () => {
		const newContacts = [...internalContacts];
		newContacts.push("");
		setInternalContacts(newContacts);
	};

	const handleDelete = (position: number) => {
		const newContacts = internalContacts.filter((_, index) => index !== position);
		setInternalContacts(newContacts);
		if (newContacts.every(Boolean) && !isEqual(newContacts.sort(compareAlphabetically), contacts.sort(compareAlphabetically))) {
			mutate(newContacts);
		}
	};

	useEffect(() => {
		setInternalContacts(contacts);
		return () => {
			setInternalContacts([]);
		};
	}, [locationId, contacts]);

	return (
		<Grid container direction="row" className="!mt-[16px]" alignItems="flex-start">
			<Grid item className="position-mapping-payroll-column">
				<MultipleSelectGroup
					allowDeleteFirst
					disableClearable
					showDeleteIcon
					showErrorMessage
					selectBy={"_id"}
					showBy={"label"}
					orderBy={"label"}
					errorMessage={"Please select at least one correspondence"}
					identifier={"technicalContacts"}
					items={internalContacts.map((item) => {
						const label = users.find((user) => user._id === item)?.fullName;
						if (label) {
							return { _id: item, label };
						}
						return { _id: item, label: "" };
					})}
					selectableOptions={possibleContacts}
					placeholder={"Select an Option"}
					onChange={handleUpdate}
					onDelete={handleDelete}
					onAdd={handleAdd}
					disabled={disabled || isPending}
					disableAddButton={isPending || internalContacts.some((item) => !item)}
					errorsList={internalContacts.map((item) => !item)}
					hideTooltip={true}
					sxSelect={{ minWidth: 216 }}
				/>
			</Grid>
		</Grid>
	);
});

export default TechnicalContacts;
